import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import PortalShell from "../../components/PortalShell";
import { toast } from "sonner";
import {
  Loader2, Target, BookOpen, Clock, Flame, Star, AlertCircle, CheckCircle2,
  ClipboardCheck, Camera, Hand, Award, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";

// ── Helpers ────────────────────────────────────────────────────────────────

const GRADE_COLOUR = { "9":"#15803d","8":"#16a34a","7":"#65a30d","6":"#ca8a04","5":"#ea580c","4":"#dc2626","3":"#b91c1c","2":"#991b1b","1":"#7f1d1d","U":"#64748b" };

function accColour(acc) {
  if (acc >= 80) return "text-green-600";
  if (acc >= 60) return "text-amber-600";
  return "text-red-600";
}

function accBg(acc) {
  if (acc >= 80) return "bg-green-50 border-green-200";
  if (acc >= 60) return "bg-amber-50 border-amber-200";
  return "bg-red-50 border-red-200";
}

/**
 * Homework and tests from the question bank - this student's own figures.
 *
 * Nothing here compares them to anybody else, and that is deliberate rather
 * than an omission. A child's useful comparison is their own last piece of
 * work; a class rank tells a fourteen-year-old nothing they can act on, and it
 * is the figure that turns a parents' evening into an argument. The tutor's
 * Progress screen holds the comparison, because deciding who needs help is a
 * question about the group.
 *
 * Marks here are the ones a person gave. Where the tutor has marked, that is
 * the figure. Where only the child has marked themselves against the mark
 * scheme, it says so.
 */
function MyWork() {
  const [d, setD] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api.get("/progress/work")
      .then((r) => { setD(r.data); setState("ok"); })
      .catch(() => setState("failed"));
  }, []);

  if (state === "loading") {
    return (
      <div className="bg-white rounded-xl border border-slate-200 py-10 flex justify-center mb-8">
        <Loader2 size={20} className="animate-spin text-slate-400" />
      </div>
    );
  }
  if (state === "failed") return null;

  const s = d.summary;
  if (s.assigned === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl py-12 text-center mb-8">
        <ClipboardCheck size={30} className="mx-auto text-slate-300 mb-3" />
        <div className="font-semibold text-slate-700">No homework set yet</div>
        <div className="text-sm text-slate-400 mt-1">
          When your tutor sets work from the question bank, your marks will appear here.
        </div>
      </div>
    );
  }

  const headline = s.marked.pct != null ? s.marked.pct : s.self.pct;
  // The fraction goes with the percentage. 100% off a single two-mark
  // question is true and misleading on its own, and a child reading it is
  // the person least able to spot a thin denominator.
  const headlineFrom = s.marked.pct != null
    ? `${s.marked.awarded} of ${s.marked.available} marks`
    : `${s.self.awarded} of ${s.self.available} marks, your own marking`;
  const colour = headline == null ? "#64748b"
    : headline >= 80 ? "#15803d" : headline >= 60 ? "#ca8a04" : "#dc2626";

  return (
    <div className="mb-10">
      <h2 className="text-sm font-semibold text-slate-700 mb-3">My homework and tests</h2>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiTile label="Marks earned" colour={colour} icon={Award}
          value={headline == null ? "-" : `${headline}%`}
          sub={headline == null ? "nothing marked yet" : headlineFrom} />
        <KpiTile label="Handed in" colour="#16a34a" icon={Hand}
          value={`${s.handed_in}/${s.assigned}`}
          sub={s.outstanding ? `${s.outstanding} still to do` : "all up to date"} />
        <KpiTile label="Still to do" colour={s.overdue ? "#dc2626" : "#64748b"} icon={Clock}
          value={s.outstanding} sub={s.overdue ? `${s.overdue} past the due date` : null} />
        <KpiTile label="Photos uploaded" colour="#7c3aed" icon={Camera} value={s.photos}
          sub="of your working" />
        {/* Not a second "marks so far" - the headline already is that, and two
            totals built from different tiers sitting side by side read as a
            contradiction. This is the one figure the headline cannot show. */}
        <KpiTile label="Checked instantly" colour="#0891b2" icon={Sparkles}
          value={s.auto.confirmed || 0}
          sub={s.auto.confirmed ? "marks matched the mark scheme" : "none matched yet"} />
      </div>

      <p className="mt-3 text-xs text-slate-500">
        {s.auto.confirmed > 0 && (
          <>
            <strong className="text-green-700">{s.auto.confirmed} marks</strong> were checked the moment you
            handed in, because your answer matched the mark scheme exactly.{" "}
          </>
        )}
        {s.tutor.pct != null && <>Your tutor has marked you <strong>{s.tutor.pct}%</strong>. </>}
        {s.self.pct != null && <>You gave yourself <strong>{s.self.pct}%</strong> on the rest. </>}
        Anything the checker could not confirm is waiting for your tutor &mdash; it is not marked wrong.
      </p>

      {s.history.length > 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mt-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">How you are doing over time</h3>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={s.history} margin={{ top: 8, right: 14, bottom: 4, left: -18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  formatter={(v, _n, pl) => [`${v}% (${pl.payload.awarded} of ${pl.payload.available})`, pl.payload.title]} />
                <Line type="monotone" dataKey="pct" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            One point per worksheet, in the order you did them. Only marked work appears.
          </p>
        </div>
      )}

      {s.topics.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mt-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Where to put your practice next</h3>
          <div className="space-y-3">
            {s.topics.slice(0, 10).map((t) => {
              const c = t.pct == null ? "#cbd5e1"
                : t.pct >= 80 ? "#15803d" : t.pct >= 60 ? "#ca8a04" : "#dc2626";
              return (
                <div key={t.topic}>
                  <div className="flex justify-between items-baseline text-xs">
                    <span className="font-medium text-slate-700 truncate pr-2">{t.topic}</span>
                    <span className="font-bold shrink-0" style={{ color: c }}>
                      {t.pct == null ? "-" : `${t.pct}%`}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full rounded-full" style={{ width: `${t.pct || 0}%`, background: c }} />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {t.awarded} of {t.available} marks{t.source === "self" ? " - your own marking" : ""}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 mt-3">
            Weakest first. These are your own marks only - nobody else's work is in this list.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function KpiTile({ label, value, icon: Icon, colour, sub }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3" style={{ borderTopColor: colour, borderTopWidth: 3 }}>
      <div className="rounded-lg p-2 shrink-0" style={{ background: colour + "18" }}>
        <Icon size={18} style={{ color: colour }} />
      </div>
      <div className="min-w-0">
        <div className="text-xl font-bold text-slate-900">{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
        {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-lg text-xs">
      <div className="text-slate-500 mb-1">{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} className="font-semibold" style={{ color: p.color }}>{p.name}: {p.value}%</div>
      ))}
    </div>
  );
}

function TopicBar({ topic, accuracy, attempts, mastered, due_for_review, days_since }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-slate-700 truncate">{topic}</span>
          {due_for_review && (
            <span className="shrink-0 text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
              Review due
            </span>
          )}
          {mastered && !due_for_review && (
            <CheckCircle2 size={11} className="text-green-500 shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-2 ml-2 shrink-0">
          <span className="text-slate-400">{attempts} quiz{attempts !== 1 ? "zes" : ""}</span>
          <span className={`font-bold ${accColour(accuracy)}`}>{accuracy}%</span>
        </div>
      </div>
      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${accuracy}%`, background: accuracy >= 80 ? "#16a34a" : accuracy >= 60 ? "#d97706" : "#dc2626" }}
        />
      </div>
      {days_since < 999 && (
        <div className="text-[10px] text-slate-400">Last practised {days_since === 0 ? "today" : `${days_since}d ago`}</div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function StudentAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/analytics/me")
      .then(r => setData(r.data))
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <PortalShell role="student" title="My Analytics" subtitle="Your personal performance breakdown">
      <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-slate-400" /></div>
    </PortalShell>
  );

  const kpis = data?.kpis || {};
  const gradeColour = GRADE_COLOUR[kpis.predicted_grade] || "#64748b";
  const dueForReview = data?.due_for_review || [];
  const topicMastery = data?.topic_mastery || [];
  const radar = data?.radar || [];
  const trend = data?.trend || [];
  const rolling = data?.rolling_avg || [];

  const noData = !data?.trend?.length;

  return (
    <PortalShell role="student" title="My Analytics" subtitle="Your own progress">

      <MyWork />

      {/* The practice quiz is switched off (`STUDENT_PAGES_OFF` in App.js),
          so for every child this whole half can only ever read 0% accuracy,
          0 quizzes done and a predicted grade of U. Telling a child on their
          first evening that they are heading for a U, on the strength of work
          they were never allowed to do, is worse than showing them nothing.
          It renders only where quiz data actually exists. */}
      {!noData && (<>
      <h2 className="text-sm font-semibold text-slate-700 mb-3">Practice quizzes</h2>
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
        <KpiTile label="Accuracy" value={`${kpis.accuracy ?? 0}%`} icon={Target} colour="#2563eb" />
        <KpiTile label="Quizzes Done" value={kpis.total_attempts ?? 0} icon={BookOpen} colour="#7c3aed" />
        <KpiTile
          label="Practice Time"
          value={kpis.practice_minutes >= 60 ? `${Math.floor(kpis.practice_minutes / 60)}h ${kpis.practice_minutes % 60}m` : `${kpis.practice_minutes ?? 0}m`}
          icon={Clock} colour="#0891b2"
        />
        <KpiTile label="Day Streak" value={`${kpis.streak_days ?? 0} days`} icon={Flame} colour="#ea580c" />
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 lg:col-span-1" style={{ borderTopColor: gradeColour, borderTopWidth: 3 }}>
          <div className="rounded-lg p-2 shrink-0" style={{ background: gradeColour + "18" }}>
            <Star size={18} style={{ color: gradeColour }} />
          </div>
          <div>
            <div className="text-2xl font-bold" style={{ color: gradeColour }}>Grade {kpis.predicted_grade}</div>
            <div className="text-xs text-slate-500 mt-0.5">Predicted GCSE</div>
            <div className="text-xs text-slate-400">based on current accuracy</div>
          </div>
        </div>
      </div>

        <div className="space-y-6">
          {/* Review alerts */}
          {dueForReview.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-2">
                <AlertCircle size={15} />
                {dueForReview.length} topic{dueForReview.length !== 1 ? "s" : ""} due for review
              </div>
              <div className="flex flex-wrap gap-2">
                {dueForReview.map(d => (
                  <span key={d.topic} className="text-xs bg-white border border-amber-200 text-amber-700 px-2.5 py-1 rounded-full font-medium">
                    {d.topic} · {d.days_since}d ago
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Score trend */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-sm font-semibold text-slate-700">Score Trend</h2>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-400 inline-block rounded" /> Score</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-slate-400 inline-block rounded border-dashed border-b" /> 7-quiz avg</span>
                </div>
              </div>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend.map((t, i) => ({ ...t, avg: rolling[i]?.avg }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" fontSize={10} stroke="#94a3b8" tickFormatter={d => d.slice(5)} />
                    <YAxis domain={[0, 100]} fontSize={10} stroke="#94a3b8" tickFormatter={v => `${v}%`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="pct" name="Score" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="avg" name="7-quiz avg" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 2" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Radar */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-sm font-semibold text-slate-700 mb-5">Topic Overview</h2>
              {radar.length >= 3 ? (
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radar}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="topic" tick={{ fontSize: 10, fill: "#64748b" }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: "#94a3b8" }} />
                      <Radar name="Accuracy" dataKey="accuracy" stroke="#2563eb" fill="#2563eb" fillOpacity={0.15} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-slate-400">
                  Practise 3+ topics to see the radar chart
                </div>
              )}
            </div>
          </div>

          {/* Topic mastery */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-sm font-semibold text-slate-700">Topic Mastery</h2>
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" /> ≥80% mastered</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" /> 60–79%</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" /> &lt;60%</span>
              </div>
            </div>
            <div className="space-y-4">
              {[...topicMastery].reverse().map(t => (
                <TopicBar key={t.topic} {...t} />
              ))}
            </div>
          </div>
        </div>
      </>)}
    </PortalShell>
  );
}
